import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TherapistWithSpecialties, Specialty } from "@shared/schema";
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import TherapistCard from "@/components/therapist-card";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function TherapistsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>("all");
  
  // Fetch all therapists
  const { data: therapists, isLoading: therapistsLoading } = useQuery<TherapistWithSpecialties[]>({
    queryKey: ["/api/therapists"],
  });
  
  // Fetch all specialties
  const { data: specialties, isLoading: specialtiesLoading } = useQuery<Specialty[]>({
    queryKey: ["/api/specialties"],
  });
  
  // Filter therapists based on search term and selected specialty
  const filteredTherapists = therapists?.filter(therapist => {
    const matchesSearch = therapist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        therapist.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        therapist.bio.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSpecialty = selectedSpecialty === "all" ||
                          therapist.specialties.some(s => s.id.toString() === selectedSpecialty);
    
    return matchesSearch && matchesSpecialty;
  });
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow bg-neutral">
        <section className="container mx-auto px-4 py-8 md:py-12">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">Terapeutas</h1>
            <p className="text-gray-600">Conheça nossos profissionais disponíveis para sessões online</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, especialidade ou palavra-chave..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="w-full md:w-1/3">
                <Select
                  value={selectedSpecialty}
                  onValueChange={setSelectedSpecialty}
                  disabled={specialtiesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas especialidades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas especialidades</SelectItem>
                    {specialties?.map(specialty => (
                      <SelectItem key={specialty.id} value={specialty.id.toString()}>
                        {specialty.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {(therapistsLoading || specialtiesLoading) ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredTherapists && filteredTherapists.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTherapists.map(therapist => (
                  <TherapistCard key={therapist.id} therapist={therapist} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Nenhum terapeuta encontrado com os filtros selecionados.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
